import type {
  ChildAgeId,
  Gender,
  HaveChildren,
  InterestOptionId,
  SelectableItem,
  TailorRecommendations,
  WorkType,
  YesNo,
} from '@physitrack/championhealth-types';

import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthContext } from '../../shared/context/auth-context';
import { Checkbox } from '../questions/checkbox/Checkbox';
import { MeasurementsInput } from '../questions/measurementsInput/MeasurementsInput';
import { NumberSelectQuestion } from '../questions/numberSelectQuestion/NumberSelectQuestion';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function AssessmentProfile({
  active,
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onOpenModal,
  onSetValue,
  dataTestId,
}: AssessmentPageItem) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (auth.isLoggedIn && !assessmentValues.age) {
      setShowOnboarding(true);
    } else {
      setShowOnboarding(false);
    }
  }, [showOnboarding, auth.isLoggedIn, assessmentValues.age]);

  const genderOptions: SelectableItem<Gender>[] = [
    { id: 'woman', title: t('A001') },
    { id: 'man', title: t('A002') },
    { id: 'trans-woman', title: t('A003') },
    { id: 'trans-man', title: t('A004') },
    { id: 'non-binary', title: t('A005') },
    { id: 'other', title: t('A006') },
    { id: 'none', title: t('A007') },
  ];

  const sexOptions: SelectableItem<TailorRecommendations>[] = [
    { id: 'male_specific', title: t('A008') },
    { id: 'female_specific', title: t('A009') },
    { id: 'do_not_tailor', title: t('A010') },
  ];

  const bmiOptions: SelectableItem<YesNo>[] = [
    { id: 'yes', title: t('A011') },
    { id: 'no', title: t('A012') },
  ];

  const childrenOptions: SelectableItem<HaveChildren>[] = [
    { id: 'yes', title: t('A013') },
    { id: 'no', title: t('A014') },
    { id: 'prefer_not_to_say', title: t('A015') },
  ];

  const childAgeOptions: SelectableItem<ChildAgeId>[] = [
    { id: '0', title: t('A016') },
    { id: '1_5', title: t('A017') },
    { id: '6_12', title: t('A018') },
    { id: '13_17', title: t('A019') },
    { id: '18', title: t('A020') },
  ];

  const workTypeOptions: SelectableItem<WorkType>[] = [
    { id: 'office', title: t('A021') },
    { id: 'home', title: t('A022') },
    { id: 'office_and_home', title: t('A023') },
    { id: 'manual', title: t('A024') },
    { id: 'driver', title: t('A025') },
    { id: 'healthcare', title: t('A026') },
    { id: 'retail', title: t('A027') },
    { id: 'education', title: t('A028') },
    { id: 'other', title: t('A029') },
  ];

  const interestOptions: SelectableItem<InterestOptionId>[] = [
    { id: 'none', title: t('A030') },
    { id: 'physical_wellbeing', title: t('A031') },
    { id: 'mental_wellbeing', title: t('A032') },
    { id: 'spiritual_wellbeing', title: t('A033') },
    { id: 'exercise_activity', title: t('A034') },
    { id: 'managing_weight', title: t('A035') },
    { id: 'nutrition', title: t('A036') },
    { id: 'hydration_alcohol', title: t('A037') },
    { id: 'sleep', title: t('A038') },
    { id: 'productivity', title: t('A039') },
    { id: 'another_area', title: t('A040') },
  ];

  useEffect(() => {
    if (assessmentValues.have_children !== 'yes') {
      onSetValue({ id: 'child_ages', value: [] });
    }
  }, [assessmentValues.have_children, onSetValue]);

  useEffect(() => {
    if (
      assessmentValues.height === 'skipped' ||
      assessmentValues.height === 'unsure' ||
      assessmentValues.weight === 'skipped' ||
      assessmentValues.weight === 'unsure'
    ) {
      onSetValue({ id: 'show_bmi', value: null });
    }
  }, [assessmentValues.height, assessmentValues.weight, onSetValue]);

  return (
    <div className={`question_container ${active}`} id={questionid} data-testid={dataTestId}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>

      <NumberSelectQuestion
        questionid="age"
        questionTitle={t('assessment.question.age')}
        minlimit={16}
        maxlimit={120}
        width=""
        modalText={t('A045')}
        onOpenModal={onOpenModal}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        controlsInfo={t('A137')}
        labelText={t('assessment.question.age.label')}
      />

      <Radio
        questionid="gender"
        width="_wide"
        questionTitle={t('assessment.question.gender')}
        options={genderOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A047')}
        onOpenModal={onOpenModal}
      />

      <Radio
        questionid="tailor_recommendations"
        width="_wide"
        questionTitle={t('assessment.question.tailor_recommendations')}
        questionTitleSuffix={t('A049')}
        options={sexOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        onOpenModal={onOpenModal}
      />

      <MeasurementsInput
        type="height"
        questionid="height"
        questionTitle={t('assessment.question.height')}
        skipText={t('A051')}
        skipText2={t('A052')}
        type1Text={t('assessment.question.height.type1')}
        type2Text={t('assessment.question.height.type2')}
        width="_wide"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A053')}
        onOpenModal={onOpenModal}
      />

      <MeasurementsInput
        type="weight"
        questionid="weight"
        questionTitle={t('assessment.question.weight')}
        skipText={t('A055')}
        skipText2={t('A056')}
        type1Text={t('assessment.question.weight.type1')}
        type2Text={t('assessment.question.weight.type2')}
        width="_wide"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A057')}
        onOpenModal={onOpenModal}
      />

      {assessmentValues.height === 'skipped' ||
      assessmentValues.height === 'unsure' ||
      assessmentValues.weight === 'skipped' ||
      assessmentValues.weight === 'unsure' ? (
        <span> </span>
      ) : (
        <Radio
          questionid="show_bmi"
          width="_wide"
          questionTitle={t('assessment.question.show_bmi')}
          questionTitleSuffix={t('A059')}
          options={bmiOptions}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          onOpenModal={onOpenModal}
        />
      )}

      <Radio
        questionid="have_children"
        width=""
        questionTitle={t('assessment.question.have_children')}
        options={childrenOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A061')}
        onOpenModal={onOpenModal}
      />
      {assessmentValues.have_children === 'yes' && (
        <Checkbox
          questionid="child_ages"
          width=""
          questionTitle={t('assessment.question.child_ages')}
          questionTitleSuffix={t('A063')}
          options={childAgeOptions}
          itemLimit="2"
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          fadeIn="yes"
        />
      )}

      <Radio
        questionid="work_type"
        width="_wide"
        questionTitle={t('assessment.question.work_type')}
        options={workTypeOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A066')}
        onOpenModal={onOpenModal}
      />

      <Checkbox
        questionid="your_interests"
        width="_wide"
        questionTitle={t('assessment.question.your_interests')}
        questionTitleSuffix={t('A068')}
        options={interestOptions}
        itemLimit="3"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('A070')}
        onOpenModal={onOpenModal}
      />
    </div>
  );
}
